import React, { useCallback, useEffect, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { ROUTES } from '../../constants/routes';
import { SESSION_EXTENDED_EVENT } from '../../services/api';

// Server-side session timeout is 20 minutes (web.xml). Warn five minutes
// before the server deadline, regardless of browser-only activity.
const DEFAULT_SESSION_TIMEOUT_MS = 20 * 60 * 1000;
const WARN_BEFORE_TIMEOUT_MS = 5 * 60 * 1000;

const formatCountdown = (ms) => {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

/**
 * Watches the server session deadline and surfaces a modal warning five
 * minutes before expiry. Browser-only activity does not extend the server
 * session; only a successful backend request or Stay signed in does.
 *
 * Designed to be mounted once inside AppLayout; gated by AppLayout on the
 * user being authenticated and MFA-passed so it never fires on /login or /mfa.
 */
const SessionTimeoutWarning = () => {
  const { logout, refreshSession } = useAuth();
  const navigate = useNavigate();
  const [sessionTimeoutMs, setSessionTimeoutMs] = useState(DEFAULT_SESSION_TIMEOUT_MS);
  const [sessionExpiresAt, setSessionExpiresAt] = useState(() => Date.now() + DEFAULT_SESSION_TIMEOUT_MS);
  const [now, setNow] = useState(Date.now());
  const [showWarning, setShowWarning] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  const redirectExpired = useCallback(() => {
    navigate(`${ROUTES.LOGIN}?reason=expired`, { replace: true });
  }, [navigate]);

  const handleTimeout = useCallback(async () => {
    if (signingOut) return;
    setSigningOut(true);
    try {
      await logout();
    } catch (err) {
      // Swallow — we're navigating to login regardless.
      console.warn('Auto-logout failed; redirecting anyway.', err);
    } finally {
      navigate(`${ROUTES.LOGIN}?reason=timeout`, { replace: true });
    }
  }, [logout, navigate, signingOut]);

  const resetSessionDeadline = useCallback((detail = {}) => {
    const timeoutMs = Number.isFinite(detail.sessionTimeoutSeconds) && detail.sessionTimeoutSeconds > 0
      ? detail.sessionTimeoutSeconds * 1000
      : sessionTimeoutMs;
    const expiresAt = Number.isFinite(detail.sessionExpiresAtEpochMillis) && detail.sessionExpiresAtEpochMillis > 0
      ? detail.sessionExpiresAtEpochMillis
      : Date.now() + timeoutMs;

    setSessionTimeoutMs(timeoutMs);
    setSessionExpiresAt(expiresAt);
    setNow(Date.now());
    setShowWarning(false);
    setSigningOut(false);
  }, [sessionTimeoutMs]);

  useEffect(() => {
    const handleSessionExtended = (event) => resetSessionDeadline(event.detail);

    window.addEventListener(SESSION_EXTENDED_EVENT, handleSessionExtended);
    return () => window.removeEventListener(SESSION_EXTENDED_EVENT, handleSessionExtended);
  }, [resetSessionDeadline]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setNow(Date.now());
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, []);

  useEffect(() => {
    const remainingMs = sessionExpiresAt - now;

    if (remainingMs <= 0) {
      handleTimeout();
      return;
    }

    setShowWarning(remainingMs <= WARN_BEFORE_TIMEOUT_MS);
  }, [handleTimeout, now, sessionExpiresAt]);

  const handleStaySignedIn = useCallback(async () => {
    try {
      // Refresh the server-side session by hitting an endpoint; this also
      // verifies the session is still valid before we hide the modal.
      const session = await refreshSession();
      if (!session.authenticated || !session.passedMfa) {
        redirectExpired();
        return;
      }
      resetSessionDeadline(session);
    } catch (err) {
      console.warn('Session refresh failed; signing out.', err);
      redirectExpired();
    }
  }, [refreshSession, resetSessionDeadline, redirectExpired]);

  const handleSignOutNow = useCallback(async () => {
    setShowWarning(false);
    await handleTimeout();
  }, [handleTimeout]);

  if (!showWarning) return null;

  const remainingMs = Math.max(0, sessionExpiresAt - now);

  return (
    <Modal
      show={showWarning}
      backdrop="static"
      keyboard={false}
      centered
      aria-labelledby="session-timeout-title"
    >
      <Modal.Header>
        <Modal.Title id="session-timeout-title">Session about to expire</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>
          You&apos;ve been inactive for a while. For your security, you&apos;ll
          be signed out automatically in:
        </p>
        <p className="display-6 text-center my-3" aria-live="polite">
          {formatCountdown(remainingMs)}
        </p>
        <p className="mb-0">Would you like to stay signed in?</p>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={handleSignOutNow} disabled={signingOut}>
          Sign out
        </Button>
        <Button variant="primary" onClick={handleStaySignedIn} disabled={signingOut}>
          Stay signed in
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SessionTimeoutWarning;
